/**
 * iter-58 — Offline scaffolding (lightweight, NOT a full sync system).
 *
 * Spec is explicit: "prepare structure for local storage of pending
 * uploads + future sync queue, do NOT fully implement sync system yet."
 *
 * What this module gives you NOW:
 *   • A typed queue of "pending upload" descriptors persisted in
 *     localStorage. Each entry contains everything the server needs
 *     to retry the upload (mode, scene_id, character_id, image data
 *     URLs, batch metadata).
 *   • `isOfflineError(err)` — distinguishes network failures from API
 *     errors so call sites only enqueue when retry is meaningful.
 *   • A tiny `useOfflineQueue` hook + `useNetworkStatus` hook so any
 *     surface can render an "N pending" pill or a "back online" cue
 *     without coupling to the queue internals.
 *
 * What this module DOES NOT do (deferred):
 *   • No automatic background flush. There is no service-worker, no
 *     visibilitychange retry, no exponential backoff. The next iter
 *     adds that on top of this scaffold.
 *   • No conflict resolution. The future sync worker will decide what
 *     to do when, e.g., a scene the entry references was deleted while
 *     offline.
 *   • Storage is plain `localStorage` — fine for small batches but
 *     capped around 5 MB on most browsers. The intentional next step
 *     is to swap the four read/write helpers below for IndexedDB
 *     (idb-keyval works well) without changing call sites. The
 *     queue's public shape is identical; only the storage layer
 *     changes. See `_readAll` / `_writeAll` below.
 */

const LS_KEY = "onsetflow:pending-uploads:v1";

/**
 * Pending-upload entry shape.
 *
 *   id         — local UUID, used to reconcile after a successful flush
 *   project_id — required by every endpoint; queue is global across
 *                projects so a user with multiple projects open offline
 *                doesn't lose work
 *   created_at — ISO timestamp; used for surface ordering and "X minutes ago"
 *   kind       — currently always "state_entry" — placeholder for future
 *                kinds like "scene_note" or "character_note"
 *   payload    — the body that would have been sent to the server,
 *                including base64 image_data + thumbnail_data
 *   batch_id   — optional grouping key when multiple entries were
 *                captured together (so the future flush worker can
 *                report "Batch of 5 retried")
 *   last_error — message from the last failed attempt (null until tried)
 *   attempts   — int; reserved for the future flush worker
 */

function _readAll() {
    try {
        const raw = window.localStorage.getItem(LS_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch (err) {
        // Quota-exceeded or corrupt JSON. Surface to console but never
        // throw — offline scaffolding must never break the UI.
        console.warn("[offline] failed to read pending uploads:", err);
        return [];
    }
}

function _writeAll(items) {
    try {
        window.localStorage.setItem(LS_KEY, JSON.stringify(items || []));
        // Notify any in-page listeners (the storage event only fires
        // across tabs natively).
        window.dispatchEvent(new CustomEvent("onsetflow:offline-queue-changed"));
        return true;
    } catch (err) {
        console.warn("[offline] failed to persist pending uploads:", err);
        return false;
    }
}

function _uuid() {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
        return crypto.randomUUID();
    }
    // Fallback: timestamp-suffix. Good enough for queue keys.
    return `pu_${Date.now().toString(36)}_${Math.random()
        .toString(36)
        .slice(2, 10)}`;
}

export function enqueuePendingUpload({ project_id, payload, batch_id = null, kind = "state_entry" }) {
    if (!project_id || !payload) return null;
    const items = _readAll();
    const item = {
        id: _uuid(),
        kind,
        project_id,
        batch_id,
        payload,
        created_at: new Date().toISOString(),
        last_error: null,
        attempts: 0,
    };
    items.push(item);
    _writeAll(items);
    return item;
}

export function getPendingUploads({ project_id } = {}) {
    const items = _readAll();
    if (!project_id) return items;
    return items.filter((i) => i.project_id === project_id);
}

export function pendingUploadCount({ project_id } = {}) {
    return getPendingUploads({ project_id }).length;
}

export function removePendingUploads(ids) {
    if (!ids || ids.length === 0) return 0;
    const set = new Set(ids);
    const items = _readAll();
    const next = items.filter((i) => !set.has(i.id));
    if (next.length === items.length) return 0;
    _writeAll(next);
    return items.length - next.length;
}

export function clearPendingUploads({ project_id } = {}) {
    if (!project_id) {
        _writeAll([]);
        return;
    }
    const items = _readAll();
    const next = items.filter((i) => i.project_id !== project_id);
    _writeAll(next);
}

/**
 * Best-effort detection of "network unavailable" vs "API rejected the
 * request". We treat it as offline if:
 *   • navigator.onLine is explicitly false, OR
 *   • axios fired the request but never got a response (no err.response).
 * That matches the way axios surfaces no-network errors and avoids
 * false-positives where the API returned 4xx/5xx (those should still
 * surface as errors to the user, NOT silently queue).
 */
export function isOfflineError(err) {
    if (!err) return false;
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
        return true;
    }
    // axios convention
    if (err && err.isAxiosError && !err.response) return true;
    if (err && err.code === "ERR_NETWORK") return true;
    if (err && err.message && /network|fetch failed|failed to fetch/i.test(err.message)) {
        return true;
    }
    return false;
}

/**
 * Iter-157I P0 — Upload queue flush worker.
 *
 * Walks the queue once, retrying each item against its canonical
 * endpoint. Items that succeed are removed; items that fail with a
 * permanent (non-network) error are marked `last_error` so the UI
 * can surface a "Failed" pill instead of "Queued". Items that fail
 * with a network error are left in place and retried on the next
 * tick — same as the original capture-time behaviour.
 *
 * Strictly best-effort. Never throws. Returns a small summary the
 * caller can toast / log.
 *
 * Triggered from the `online` + `visibilitychange` listeners
 * registered by `startFlushWorker()` below, plus a manual
 * "Retry now" affordance.
 */
let _flushInFlight = false;
export async function flushPendingUploads(apiClient) {
    if (_flushInFlight) return { skipped: true };
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
        return { skipped: true, reason: "offline" };
    }
    const items = _readAll();
    if (items.length === 0) return { processed: 0, succeeded: 0, failed: 0 };
    _flushInFlight = true;
    let succeeded = 0;
    let failed = 0;
    const successIds = [];
    try {
        for (const item of items) {
            try {
                if (item.kind === "state_entry") {
                    await apiClient.post(
                        `/projects/${item.project_id}/state-entries`,
                        item.payload,
                    );
                    successIds.push(item.id);
                    succeeded++;
                } else {
                    // Unknown kind — leave in queue so a future
                    // schema migration can pick it up.
                }
            } catch (err) {
                if (isOfflineError(err)) {
                    // Still no network. Stop early to preserve
                    // ordering and avoid hammering.
                    break;
                }
                // Permanent failure. Stamp last_error so the UI
                // can show "Failed" instead of "Queued" — the user
                // now knows what actually happened.
                item.last_error =
                    err?.response?.data?.detail ||
                    err?.message ||
                    "Upload failed";
                item.attempts = (item.attempts || 0) + 1;
                failed++;
            }
        }
        if (successIds.length) removePendingUploads(successIds);
        if (failed) {
            // Persist the last_error stamps we mutated above.
            const fresh = _readAll();
            const failedById = new Map(items.filter((i) => i.last_error).map((i) => [i.id, i]));
            for (const f of fresh) {
                const mutated = failedById.get(f.id);
                if (mutated) {
                    f.last_error = mutated.last_error;
                    f.attempts = mutated.attempts;
                }
            }
            _writeAll(fresh);
        }
        return { processed: items.length, succeeded, failed };
    } finally {
        _flushInFlight = false;
    }
}

/**
 * Iter-157I P0 — register window-level retry triggers. Idempotent
 * (calling twice doesn't double-register). Returns an unsubscribe.
 */
let _flushWorkerStarted = false;
export function startFlushWorker(apiClient) {
    if (_flushWorkerStarted || typeof window === "undefined") return () => {};
    _flushWorkerStarted = true;
    const run = () => {
        // Defer so the event-loop has the chance to settle (avoid
        // racing the connection-state transition).
        setTimeout(() => {
            flushPendingUploads(apiClient).catch(() => { /* swallow */ });
        }, 250);
    };
    window.addEventListener("online", run);
    window.addEventListener("visibilitychange", () => {
        if (!document.hidden) run();
    });
    // Boot-time attempt for any queue items left over from a prior
    // session.
    run();
    return () => {
        // Best-effort teardown for tests / hot-reload.
        _flushWorkerStarted = false;
        window.removeEventListener("online", run);
    };
}
